import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'

export default function CRM() {
  const [usuarios, setUsuarios] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedUsuario, setSelectedUsuario] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [usuarioDetalle, setUsuarioDetalle] = useState(null)

  useEffect(() => {
    fetchUsuarios()
  }, [])

  const fetchUsuarios = async () => {
    try {
      setLoading(true)

      // Obtener usuarios de comentarios
      const { data: comentariosData } = await supabase
        .from('comentarios')
        .select('usuario_email, usuario_nombre, created_at')

      // Obtener usuarios de inscripciones
      const { data: inscripcionesData } = await supabase
        .from('inscripciones_eventos')
        .select('usuario_email, usuario_nombre, created_at')

      // Consolidar usuarios únicos
      const usuariosMap = new Map()

      // Procesar comentarios
      comentariosData?.forEach(item => {
        if (!usuariosMap.has(item.usuario_email)) {
          usuariosMap.set(item.usuario_email, {
            email: item.usuario_email,
            nombre: item.usuario_nombre,
            ultima_actividad: item.created_at,
            total_comentarios: 1,
            total_inscripciones: 0
          })
        } else {
          const user = usuariosMap.get(item.usuario_email)
          user.total_comentarios++
          if (new Date(item.created_at) > new Date(user.ultima_actividad)) {
            user.ultima_actividad = item.created_at
          }
        }
      })

      // Procesar inscripciones
      inscripcionesData?.forEach(item => {
        if (!usuariosMap.has(item.usuario_email)) {
          usuariosMap.set(item.usuario_email, {
            email: item.usuario_email,
            nombre: item.usuario_nombre,
            ultima_actividad: item.created_at,
            total_comentarios: 0,
            total_inscripciones: 1
          })
        } else {
          const user = usuariosMap.get(item.usuario_email)
          user.total_inscripciones++
          if (new Date(item.created_at) > new Date(user.ultima_actividad)) {
            user.ultima_actividad = item.created_at
          }
        }
      })

      // Convertir a array y ordenar por última actividad
      const usuariosArray = Array.from(usuariosMap.values()).sort(
        (a, b) => new Date(b.ultima_actividad) - new Date(a.ultima_actividad)
      )

      setUsuarios(usuariosArray)
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchUsuarioDetalle = async (email) => {
    try {
      // Obtener comentarios del usuario
      const { data: comentarios } = await supabase
        .from('comentarios')
        .select(`
          *,
          tiendas(nombre)
        `)
        .eq('usuario_email', email)
        .order('created_at', { ascending: false })

      // Obtener inscripciones del usuario
      const { data: inscripciones } = await supabase
        .from('inscripciones_eventos')
        .select(`
          *,
          eventos(nombre, fecha_inicio, estado)
        `)
        .eq('usuario_email', email)
        .order('created_at', { ascending: false })

      setUsuarioDetalle({
        comentarios: comentarios || [],
        inscripciones: inscripciones || []
      })
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const openUsuarioModal = async (usuario) => {
    setSelectedUsuario(usuario)
    await fetchUsuarioDetalle(usuario.email)
    setShowModal(true)
  }

  const exportToCSV = async () => {
    if (filteredUsuarios.length === 0) return

    // Obtener TODAS las interacciones detalladas con sus relaciones
    const { data: comentariosData } = await supabase
      .from('comentarios')
      .select(`
        *,
        tiendas(nombre)
      `)
      .order('created_at', { ascending: false })

    const { data: inscripcionesData } = await supabase
      .from('inscripciones_eventos')
      .select(`
        *,
        eventos(nombre, estado)
      `)
      .order('created_at', { ascending: false })

    // Filtrar solo los usuarios que están en filteredUsuarios (respeta búsqueda)
    const emailsFiltrados = new Set(filteredUsuarios.map(u => u.email))

    // Headers del CSV expandido con detalle completo
    const headers = [
      'Nombre Usuario',
      'Email Usuario', 
      'Tipo Interacción',
      'Marca/Evento',
      'Detalle',
      'Puntuación',
      'Estado',
      'Fecha'
    ]

    const rows = []

    // Agregar todas las interacciones de comentarios
    comentariosData?.forEach(com => {
      if (emailsFiltrados.has(com.usuario_email)) {
        rows.push([
          com.usuario_nombre || '',
          com.usuario_email || '',
          'Comentario',
          com.tiendas?.nombre || 'Marca eliminada',
          com.comentario || '',
          com.puntuacion || '',
          com.publicado ? 'Publicado' : 'Pendiente',
          new Date(com.created_at).toLocaleDateString('es-PE')
        ])
      }
    })

    // Agregar todas las interacciones de inscripciones
    inscripcionesData?.forEach(ins => {
      if (emailsFiltrados.has(ins.usuario_email)) {
        rows.push([
          ins.usuario_nombre || '',
          ins.usuario_email || '',
          'Inscripción Evento',
          ins.eventos?.nombre || 'Evento eliminado',
          '',
          '',
          ins.eventos?.estado || '',
          new Date(ins.created_at).toLocaleDateString('es-PE')
        ])
      }
    })

    // Ordenar por fecha descendente (más reciente primero)
    rows.sort((a, b) => {
      const dateA = new Date(a[7].split('/').reverse().join('-'))
      const dateB = new Date(b[7].split('/').reverse().join('-'))
      return dateB - dateA
    })

    // Generar CSV con escape correcto de comillas
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `crm_interacciones_detalladas_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
  }

  const filteredUsuarios = usuarios.filter(usuario =>
    usuario.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    usuario.email.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const totalInteracciones = usuarios.reduce(
    (sum, u) => sum + u.total_comentarios + u.total_inscripciones,
    0
  )

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">CRM - GESTIÓN DE USUARIOS</h1>
          <p className="text-gray-600 mt-1">
            Vista consolidada de usuarios del sitio web
          </p>
        </div>
        <button
          onClick={exportToCSV}
          disabled={filteredUsuarios.length === 0}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
        >
          📄 Exportar CSV
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-600">Total Usuarios</p>
          <p className="text-2xl font-bold text-primary">{usuarios.length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-600">Total Comentarios</p>
          <p className="text-2xl font-bold text-orange-600">
            {usuarios.reduce((sum, u) => sum + u.total_comentarios, 0)}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-600">Total Inscripciones</p>
          <p className="text-2xl font-bold text-green-600">
            {usuarios.reduce((sum, u) => sum + u.total_inscripciones, 0)}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-600">Total Interacciones</p>
          <p className="text-2xl font-bold text-purple-600">{totalInteracciones}</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <input
          type="text"
          placeholder="Buscar por nombre o email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 border rounded-lg"
        />
      </div>

      {/* Tabla de Usuarios */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="text-center py-12">Cargando...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comentarios</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Inscripciones</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Última Actividad</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsuarios.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                      No se encontraron usuarios
                    </td>
                  </tr>
                ) : (
                  filteredUsuarios.map((usuario) => (
                    <tr key={usuario.email} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0 rounded-full bg-primary text-white flex items-center justify-center font-semibold">
                            {usuario.nombre.charAt(0).toUpperCase()}
                          </div>
                          <div className="ml-3">
                            <p className="font-medium text-gray-900">{usuario.nombre}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{usuario.email}</td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                          {usuario.total_comentarios}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                          {usuario.total_inscripciones}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {new Date(usuario.ultima_actividad).toLocaleDateString('es-PE', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => openUsuarioModal(usuario)}
                          className="text-primary hover:text-blue-800 text-sm font-medium"
                        >
                          Ver Detalle →
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Detalle de Usuario */}
      {showModal && selectedUsuario && usuarioDetalle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg w-full max-h-[85vh] overflow-hidden" style={{ maxWidth: window.innerWidth < 768 ? '100%' : '896px' }}>
            <div className="p-6 border-b flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-primary text-white flex items-center justify-center text-2xl font-bold">
                  {selectedUsuario.nombre.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{selectedUsuario.nombre}</h2>
                  <p className="text-gray-600">{selectedUsuario.email}</p>
                </div>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                type="button"
              >
                ×
              </button>
            </div>
            
            <div className="p-6 border-b">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-orange-50 p-3 rounded">
                  <p className="text-sm text-gray-600">Comentarios</p>
                  <p className="text-xl font-bold text-orange-600">{selectedUsuario.total_comentarios}</p>
                </div>
                <div className="bg-green-50 p-3 rounded">
                  <p className="text-sm text-gray-600">Inscripciones</p>
                  <p className="text-xl font-bold text-green-600">{selectedUsuario.total_inscripciones}</p>
                </div>
                <div className="bg-blue-50 p-3 rounded">
                  <p className="text-sm text-gray-600">Última Actividad</p>
                  <p className="text-sm font-bold text-blue-600">
                    {new Date(selectedUsuario.ultima_actividad).toLocaleDateString('es-PE')}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-250px)] min-h-[300px]">
              {/* Eventos Inscritos */}
              <div className="mb-6">
                <h3 className="text-lg font-bold mb-3">🎉 Eventos Inscritos ({usuarioDetalle.inscripciones.length})</h3>
                {usuarioDetalle.inscripciones.length === 0 ? (
                  <p className="text-gray-500 text-sm">No hay inscripciones</p>
                ) : (
                  <div className="space-y-2">
                    {usuarioDetalle.inscripciones.map((inscripcion) => (
                      <div key={inscripcion.id} className="border rounded-lg p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{inscripcion.eventos?.nombre || 'Evento eliminado'}</p>
                            <p className="text-sm text-gray-600">
                              Inscrito el {new Date(inscripcion.created_at).toLocaleDateString('es-PE')}
                            </p>
                          </div>
                          {inscripcion.eventos && (
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              inscripcion.eventos.estado === 'Activo' ? 'bg-green-100 text-green-800' :
                              inscripcion.eventos.estado === 'Próximo' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {inscripcion.eventos.estado}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Comentarios en Marcas */}
              <div>
                <h3 className="text-lg font-bold mb-3">💬 Comentarios en Marcas ({usuarioDetalle.comentarios.length})</h3>
                {usuarioDetalle.comentarios.length === 0 ? (
                  <p className="text-gray-500 text-sm">No hay comentarios</p>
                ) : (
                  <div className="space-y-2">
                    {usuarioDetalle.comentarios.map((comentario) => (
                      <div key={comentario.id} className="border rounded-lg p-3">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium">{comentario.tiendas?.nombre || 'Marca eliminada'}</p>
                            <p className="text-yellow-500">{'⭐'.repeat(comentario.puntuacion)}</p>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            comentario.publicado ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {comentario.publicado ? 'Publicado' : 'Pendiente'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700">{comentario.comentario}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(comentario.created_at).toLocaleDateString('es-PE')}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t">
              <button
                onClick={() => setShowModal(false)}
                className="w-full px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
